import { canvasSteps } from '@bb/contracts';
import type { AttributeSummary, EventSummary, WritePolicy } from '../../../../api/client.ts';

/**
 * User Update step settings as the editor reads and writes them (BB-120): `canvasSteps.UserUpdateConfig` of
 * `@bb/contracts`, the schema the runtime handler and the draft validation use. The panel shows the contract's rules
 * per field and filters the registry to what a Canvas may write (`canvasAttributeBlocker` / `canvasEventBlocker` of
 * `@bb/domain`, which the dashboard does not depend on); the server stays the final word on both.
 */
export type UserUpdateKind = (typeof canvasSteps.USER_UPDATE_ACTION_KINDS)[number];
export type WithdrawBy = 'message' | 'tag' | 'extras';

export type WithdrawSelector = { messageApiId: string } | { tagId: string } | { extrasKey: string; extrasValue: string };

/** One action as stored; `by` is `''` while the number field is empty (the contract then rejects it). */
export type UserUpdateFormAction =
  | { kind: 'set_attribute'; key: string; valueExpr: string }
  | { kind: 'increment_attribute'; key: string; by: number | '' }
  | { kind: 'remove_attribute'; key: string }
  | { kind: 'log_event'; name: string; propertiesExpr?: string }
  | { kind: 'withdraw_cards'; selector: WithdrawSelector; reason?: string };

export interface UserUpdateForm {
  actions: UserUpdateFormAction[];
}

export const USER_UPDATE_MAX_ACTIONS = canvasSteps.USER_UPDATE_MAX_ACTIONS;
export const USER_UPDATE_MAX_EXPRESSION = canvasSteps.USER_UPDATE_MAX_EXPRESSION;
const KEY_MAX = 255;
const EXTRAS_VALUE_MAX = 1000;
const REASON_MAX = 200;

export const KIND_LABELS: Readonly<Record<UserUpdateKind, string>> = {
  set_attribute: 'Set attribute',
  increment_attribute: 'Increment attribute',
  remove_attribute: 'Remove attribute',
  log_event: 'Log event',
  withdraw_cards: 'Withdraw cards',
};
export const KIND_OPTIONS = canvasSteps.USER_UPDATE_ACTION_KINDS.map((value) => ({ value, label: KIND_LABELS[value] }));
export const WITHDRAW_BY_OPTIONS: Array<{ value: WithdrawBy; label: string }> = [
  { value: 'extras', label: 'Extras value' },
  { value: 'message', label: 'Message (campaign)' },
  { value: 'tag', label: 'Tag' },
];

/** How a literal value is converted to the attribute's type (`coerceAttributeValue` of the handler). */
export const VALUE_HINTS: Readonly<Record<string, string>> = {
  string: 'Text, at most 255 characters.',
  number: 'A finite number, e.g. 100 or 12.5.',
  boolean: 'true or false, in any letter case.',
  time: 'An ISO date or time, or Unix seconds or milliseconds.',
  array: 'A JSON array of strings, e.g. ["EURUSD", "GBPUSD"].',
  object: 'A JSON object.',
};

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown) => (typeof v === 'string' ? v : '');
const isKind = (v: unknown): v is UserUpdateKind => (canvasSteps.USER_UPDATE_ACTION_KINDS as readonly unknown[]).includes(v);
export const isAttributeAction = (a: UserUpdateFormAction): a is Extract<UserUpdateFormAction, { key: string }> =>
  a.kind === 'set_attribute' || a.kind === 'increment_attribute' || a.kind === 'remove_attribute';

export function withdrawBy(selector: WithdrawSelector): WithdrawBy {
  return 'messageApiId' in selector ? 'message' : 'tagId' in selector ? 'tag' : 'extras';
}

export const emptySelector = (by: WithdrawBy): WithdrawSelector => (by === 'message' ? { messageApiId: '' } : by === 'tag' ? { tagId: '' } : { extrasKey: '', extrasValue: '' });

function readSelector(raw: unknown): WithdrawSelector {
  const s = isObject(raw) ? raw : {};
  if ('messageApiId' in s) return { messageApiId: str(s.messageApiId) };
  if ('tagId' in s) return { tagId: str(s.tagId) };
  return { extrasKey: str(s.extrasKey), extrasValue: str(s.extrasValue) };
}

/** One stored action with unknown keys left out; an unknown kind reads as an empty «Set attribute». */
function readAction(raw: Record<string, unknown>): UserUpdateFormAction {
  const kind = isKind(raw.kind) ? raw.kind : 'set_attribute';
  switch (kind) {
    case 'set_attribute':
      return { kind, key: str(raw.key), valueExpr: str(raw.valueExpr) };
    case 'increment_attribute':
      return { kind, key: str(raw.key), by: typeof raw.by === 'number' ? raw.by : '' };
    case 'remove_attribute':
      return { kind, key: str(raw.key) };
    case 'log_event':
      return { kind, name: str(raw.name), ...(typeof raw.propertiesExpr === 'string' ? { propertiesExpr: raw.propertiesExpr } : {}) };
    case 'withdraw_cards':
      return { kind, selector: readSelector(raw.selector), ...(typeof raw.reason === 'string' ? { reason: raw.reason } : {}) };
  }
}

export function readUserUpdate(raw: unknown): UserUpdateForm {
  const c = isObject(raw) ? raw : {};
  return { actions: (Array.isArray(c.actions) ? c.actions : []).filter(isObject).map(readAction) };
}

/** A new action of a kind; an attribute key carries over between the attribute kinds. */
export function actionOfKind(kind: UserUpdateKind, from?: UserUpdateFormAction): UserUpdateFormAction {
  const key = from && isAttributeAction(from) ? from.key : '';
  switch (kind) {
    case 'set_attribute':
      return { kind, key, valueExpr: '' };
    case 'increment_attribute':
      return { kind, key, by: 1 };
    case 'remove_attribute':
      return { kind, key };
    case 'log_event':
      return { kind, name: '' };
    case 'withdraw_cards':
      return { kind, selector: emptySelector('extras') };
  }
}

/** Messages per field of one action; keys are the config paths inside the action (`selector.extrasKey`). */
export type ActionIssues = Partial<Record<'kind' | 'key' | 'valueExpr' | 'by' | 'name' | 'propertiesExpr' | 'selector' | 'extrasKey' | 'extrasValue' | 'reason', string>>;

export interface UserUpdateIssues {
  /** About the list itself: none, more than the limit, or a contract rule the fields below do not show. */
  actions?: string;
  each: ActionIssues[];
}

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const tooLong = (max: number) => `At most ${max.toLocaleString('en-US')} characters.`;

function keyIssue(key: string): string | undefined {
  if (!key) return 'Choose an attribute.';
  if (key.length > KEY_MAX) return tooLong(KEY_MAX);
  if (key.startsWith('$')) return 'Standard attributes cannot be changed by a Canvas.';
  if (key.includes('.')) return 'Attribute keys have no dots.';
  return undefined;
}

function selectorIssues(s: WithdrawSelector): ActionIssues {
  if ('messageApiId' in s) return uuid.test(s.messageApiId) ? {} : { selector: 'Choose a message.' };
  if ('tagId' in s) return uuid.test(s.tagId) ? {} : { selector: 'Choose a tag.' };
  const own: ActionIssues = {};
  if (!s.extrasKey.trim()) own.extrasKey = 'Enter the extras key.';
  else if (s.extrasKey.trim().length > KEY_MAX) own.extrasKey = tooLong(KEY_MAX);
  if (s.extrasValue.length > EXTRAS_VALUE_MAX) own.extrasValue = tooLong(EXTRAS_VALUE_MAX);
  return own;
}

/** The contract's rules as messages per field (what `canvasSteps.UserUpdateConfig.safeParse` rejects). */
export function userUpdateIssues(c: UserUpdateForm): UserUpdateIssues {
  const out: UserUpdateIssues = { each: [] };
  if (c.actions.length === 0) out.actions = 'Add at least one action.';
  else if (c.actions.length > USER_UPDATE_MAX_ACTIONS) out.actions = `At most ${USER_UPDATE_MAX_ACTIONS} actions.`;
  const seen = new Set<string>();
  out.each = c.actions.map((a) => {
    const own: ActionIssues = {};
    const set = (field: keyof ActionIssues, message: string | undefined) => {
      if (message) own[field] = message;
    };
    if (isAttributeAction(a)) {
      set('key', keyIssue(a.key));
      if (!own.key && seen.has(a.key)) own.key = `${a.key} already has an action in this step.`;
      if (a.key) seen.add(a.key);
    }
    switch (a.kind) {
      case 'set_attribute':
        set('valueExpr', !a.valueExpr ? 'Enter a value or a Liquid expression.' : a.valueExpr.length > USER_UPDATE_MAX_EXPRESSION ? tooLong(USER_UPDATE_MAX_EXPRESSION) : undefined);
        break;
      case 'increment_attribute':
        set('by', a.by === '' || !Number.isFinite(a.by) ? 'Enter a number.' : a.by === 0 ? 'Enter a non-zero number.' : undefined);
        break;
      case 'log_event':
        set('name', !a.name.trim() ? 'Choose an event.' : a.name.trim().length > KEY_MAX ? tooLong(KEY_MAX) : a.name.trim().startsWith('$') ? 'Names starting with $ are reserved.' : undefined);
        if (a.propertiesExpr !== undefined) set('propertiesExpr', !a.propertiesExpr ? 'Enter the properties or leave them out.' : a.propertiesExpr.length > USER_UPDATE_MAX_EXPRESSION ? tooLong(USER_UPDATE_MAX_EXPRESSION) : undefined);
        break;
      case 'withdraw_cards':
        Object.assign(own, selectorIssues(a.selector));
        if (a.reason !== undefined) set('reason', !a.reason.trim() ? 'Enter a reason or leave it out.' : a.reason.trim().length > REASON_MAX ? tooLong(REASON_MAX) : undefined);
        break;
    }
    return own;
  });
  // Insurance: a contract rule the messages above miss still blocks the step instead of passing silently.
  if (!hasUserUpdateIssues(out)) {
    const parsed = canvasSteps.UserUpdateConfig.safeParse(c);
    if (!parsed.success) out.actions = parsed.error.issues.map((i) => `${i.path.join('.') || 'config'}: ${i.message}`).join('; ');
  }
  return out;
}

export const hasUserUpdateIssues = (i: UserUpdateIssues) => Boolean(i.actions) || i.each.some((a) => Object.keys(a).length > 0);

const FIELDS: ReadonlySet<string> = new Set(['kind', 'key', 'valueExpr', 'by', 'name', 'propertiesExpr', 'selector', 'extrasKey', 'extrasValue', 'reason']);

/** The server's `invalid_step_config.fields` (`actions.N.key`, …) by action and field; other paths go to the list. */
export function serverFieldIssues(fields: ReadonlyArray<{ path: string; message: string }>): UserUpdateIssues {
  const out: UserUpdateIssues = { each: [] };
  for (const f of fields) {
    const m = /^actions\.(\d+)(?:\.([a-zA-Z]+)(?:\.([a-zA-Z]+))?)?/.exec(f.path);
    if (!m) {
      out.actions = out.actions ? `${out.actions}; ${f.message}` : f.message;
      continue;
    }
    const index = Number(m[1]);
    const leaf = m[2] === 'selector' && (m[3] === 'extrasKey' || m[3] === 'extrasValue') ? m[3] : (m[2] ?? '');
    const field: keyof ActionIssues = FIELDS.has(leaf) ? (leaf as keyof ActionIssues) : 'kind';
    out.each[index] = { ...out.each[index], [field]: out.each[index]?.[field] ?? f.message };
  }
  return out;
}

// What a Canvas may write.

export interface WritableCatalog {
  /** Custom attributes a Canvas may write, by key. */
  attributes: Map<string, AttributeSummary>;
  /** Events a Canvas may log, by name. */
  events: Map<string, EventSummary>;
  /** Why the rest of the registry is not offered, by attribute key or event name. */
  refused: { attributes: Map<string, string>; events: Map<string, string> };
}

const POLICY_REASON: Readonly<Record<Exclude<WritePolicy, 'any'>, string>> = {
  server_only: 'only the backend writes it (trusted)',
  server_only_report: 'only the backend should write it (trusted)',
};

/**
 * The registry split into what a Canvas may write and why the rest is refused: attributes with write policy `any`,
 * PII class `none` and a status other than `blocked`; events with write policy `any` and a status other than `blocked`
 * or `test_only`. `policies` are the non-`any` write policies of the trusted data report (the event list does not
 * carry them); an attribute's own `writePolicy` is used when the report does not name it.
 */
export function writableCatalog(attributes: readonly AttributeSummary[], events: readonly EventSummary[], policies: ReadonlyMap<string, WritePolicy>): WritableCatalog {
  const out: WritableCatalog = { attributes: new Map(), events: new Map(), refused: { attributes: new Map(), events: new Map() } };
  for (const a of attributes) {
    if (a.kind !== 'custom') continue;
    const policy = policies.get(`attribute:${a.key}`) ?? a.writePolicy ?? 'any';
    const reason = policy !== 'any' ? POLICY_REASON[policy] : a.piiClass !== 'none' ? 'it holds personal data (PII)' : a.status === 'blocked' ? 'it is blocked' : undefined;
    if (reason) out.refused.attributes.set(a.key, reason);
    else out.attributes.set(a.key, a);
  }
  for (const e of events) {
    const policy = policies.get(`event:${e.name}`) ?? 'any';
    const reason = policy !== 'any' ? POLICY_REASON[policy] : e.status === 'blocked' ? 'it is blocked' : e.status === 'test_only' ? 'it is for tests only' : undefined;
    if (reason) out.refused.events.set(e.name, reason);
    else out.events.set(e.name, e);
  }
  return out;
}

/** Problems the registry shows before the server does: an attribute or event a Canvas may not write, incrementing a non-number. */
export function registryIssues(c: UserUpdateForm, catalog: WritableCatalog): UserUpdateIssues {
  return {
    each: c.actions.map((a) => {
      const own: ActionIssues = {};
      if (isAttributeAction(a) && a.key) {
        const refused = catalog.refused.attributes.get(a.key);
        const attribute = catalog.attributes.get(a.key);
        if (refused) own.key = `A Canvas cannot change ${a.key}: ${refused}.`;
        else if (!attribute) own.key = `${a.key} is not in the attribute registry.`;
        else if (a.kind === 'increment_attribute' && attribute.dataType !== 'number') own.key = `Only number attributes can be incremented; ${a.key} is ${attribute.dataType}.`;
      }
      if (a.kind === 'log_event' && a.name.trim()) {
        const name = a.name.trim();
        const refused = catalog.refused.events.get(name);
        if (refused) own.name = `A Canvas cannot log ${name}: ${refused}.`;
        else if (!catalog.events.has(name)) own.name = `${name} is not in the event registry.`;
      }
      return own;
    }),
  };
}

/** Contract messages first, then the registry's, then the server's (the server lags behind the last edit). */
export function mergeIssues(...sources: UserUpdateIssues[]): UserUpdateIssues {
  const out: UserUpdateIssues = { each: [] };
  for (const s of sources) {
    if (!out.actions && s.actions) out.actions = s.actions;
    s.each.forEach((a, i) => {
      out.each[i] = { ...a, ...out.each[i] };
    });
  }
  return out;
}

/** The action as the contract stores it: empty optional fields are left out. */
export function cleanAction(a: UserUpdateFormAction): UserUpdateFormAction {
  if (a.kind === 'log_event' && a.propertiesExpr === '') return { kind: a.kind, name: a.name };
  if (a.kind === 'withdraw_cards' && a.reason === '') return { kind: a.kind, selector: a.selector };
  return a;
}
